import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft, Sun } from "lucide-react";

export default function ComplaintPrint() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [co, setCo] = useState(null);

  useEffect(() => {
    api.get(`/complaints/${id}`).then(({ data }) => setC(data));
    api.get("/settings/company").then(({ data }) => setCo(data)).catch(() => {});
  }, [id]);

  if (!c) return <div className="p-10 text-center text-slate-400">Loading...</div>;

  return (
    <div className="min-h-screen bg-slate-100 py-8">
      <div className="no-print max-w-3xl mx-auto flex justify-between items-center mb-4 px-4">
        <Button variant="outline" onClick={() => nav(-1)}><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
        <Button className="bg-amber-500 hover:bg-amber-600 text-white" onClick={() => window.print()} data-testid="print-btn"><Printer className="w-4 h-4 mr-1" />Print</Button>
      </div>
      <div className="max-w-3xl mx-auto bg-white p-8 sm:p-12 shadow-lg" data-testid="complaint-print-doc">
        <div className="flex justify-between items-start border-b-4 border-amber-500 pb-5 mb-6">
          <div className="flex items-start gap-4">
            {co?.logo_url ? (
              <img src={co.logo_url} alt="MRM" className="h-16 object-contain" data-testid="print-logo" />
            ) : (
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center"><Sun className="w-8 h-8 text-white" strokeWidth={2.5} /></div>
            )}
            <div>
              <div className="text-lg font-extrabold leading-tight" style={{ fontFamily: 'Outfit' }}>{co?.company_name || "MRM RENEWABLES ENERGY PRIVATE LIMITED"}</div>
              <div className="text-xs uppercase tracking-widest text-amber-600 font-semibold">Complaint Resolution Report</div>
              {co?.address && <div className="text-[10px] text-slate-600 max-w-sm mt-1 leading-snug">{co.address}</div>}
              <div className="text-[10px] text-slate-600 mt-0.5">
                GSTIN: {co?.gstin || "-"} · Tel: {co?.phone || "-"} · {co?.email || ""}
              </div>
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-xs uppercase tracking-wider text-slate-500">Complaint No.</div>
            <div className="text-xl font-bold font-mono">{c.complaint_no}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 mb-6">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Customer</div>
            <div className="text-lg font-bold text-slate-900">{c.customer_name}</div>
            <div className="text-sm text-slate-600">{c.customer_mobile}</div>
            <div className="text-sm text-slate-600">{c.customer_address}</div>
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="text-slate-500">Type</div><div className="font-semibold">{c.complaint_type}</div>
            <div className="text-slate-500">Priority</div><div className="font-semibold">{c.priority}</div>
            <div className="text-slate-500">Status</div><div className="font-semibold">{c.status}</div>
            <div className="text-slate-500">Registered</div><div className="font-semibold">{fmtDate(c.created_at)}</div>
            <div className="text-slate-500">Visit Date</div><div className="font-semibold">{fmtDate(c.visit_date)}</div>
          </div>
        </div>

        <div className="mb-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Description</div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm">{c.description}</div>
        </div>

        <div className="mb-6">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Resolution</div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm min-h-[80px]">{c.resolution || "—"}</div>
        </div>

        {c.history?.length > 0 && (
          <div className="mb-6">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Complaint History</div>
            <div className="border border-slate-200 rounded-lg divide-y">
              {c.history.map((h, i) => (
                <div key={i} className="px-4 py-2 text-xs flex justify-between">
                  <span className="text-slate-700">{h.status} — {h.by}</span>
                  <span className="text-slate-500">{fmtDate(h.at)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-6 mt-10 pt-6 border-t border-slate-200">
          <div className="text-sm text-slate-600">
            <div className="border-t border-slate-400 pt-2 mt-8 inline-block px-6">Customer Signature</div>
          </div>
          <div className="text-right">
            <div className="border-t border-slate-400 pt-2 mt-8 inline-block px-6">
              <div className="text-xs text-slate-500">For {co?.company_name || "MRM Renewables"}</div>
              <div className="text-sm font-semibold">Authorised Signatory</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
